import React, { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@common-fe/common-fe';
import { AppButton, Box, ErrorModal, Preloader, Text } from '@common-fe/common-fe';

import { ROUTES } from '@/common';
import { SERVER_ERROR_MODAL_TEXT, SERVER_ERROR_MODAL_TITLE } from '@/common/constants';
import { PreventLeaveModal } from '@/components';
import { SuccessModal } from '@/components/elements';
import Modal from '@/components/elements/AutoBlockedModal/Modal';
import { useHistory } from '@/modules/core/hooks';

import { useCreateRoleQuery } from '../../queries';
import { QUERY_KEY as GET_BY_ID_QUERY_KEY } from '../../queries/useGetRoleById.query';
import { IRoleSetup, NO_ACCESS_ROLE_ID } from '../../role.types';
import RoleSetupForm, { AffectedUsersCountSection } from '../RoleSetupForm';
import FieldLevelAccessSection from '../RoleSetupForm/FieldLevelAccessSection';
import PermissionsSection from '../RoleSetupForm/PermissionsSection';
import { useRoleSetupStore } from '../stores';

import { useGrantedPermissions } from './hooks';

interface Props {
  isDirty?: boolean;
  visible: boolean;
  isEditMode?: boolean;
  onSetVisible: (value: boolean) => void;
}
const RoleSetupConfirmModal: React.FC<Props> = ({ isDirty, isEditMode, visible, onSetVisible }) => {
  const state = useRoleSetupStore((state) => state.state);
  const [isServerError, setIsServerError] = useState(false);
  const handleReset = useRoleSetupStore((state) => state.handleReset);
  const [isPrevent, setIsPrevent] = useState(true);
  const [successModal, setSuccessModal] = useState(false);

  const handleToggleVisible = useCallback(() => {
    onSetVisible(!visible);
  }, [onSetVisible, visible]);

  const setState = useRoleSetupStore((state) => state.setState);
  const history = useHistory();
  const handleSubmit = useCallback(
    (data: IRoleSetup) => {
      setState(data);
      history.push(ROUTES.ROLE_SETUP);
    },
    [history, setState]
  );

  const { revoked, granted } = useGrantedPermissions(state.permissions);

  const formattedData: IRoleSetup = useMemo(() => {
    return {
      ...(state as IRoleSetup),
      revoked,
      granted,
    };
  }, [granted, revoked, state]);
  const { handleSave: handleSaveRole, isLoading } = useCreateRoleQuery({
    isEditMode,
    onClose: () => {
      onSetVisible(false);
    },
    onError: () => {
      setIsServerError(true);
    },
  });
  const queryClient = useQueryClient();

  const handleConfirm = useCallback(async () => {
    try {
      setIsServerError(false);
      await handleSaveRole(state as IRoleSetup);
      setSuccessModal(true);
      onSetVisible(false);
      setIsPrevent(false);
      if (isEditMode) {
        queryClient.invalidateQueries({
          queryKey: [GET_BY_ID_QUERY_KEY],
        });
      }
    } catch {
      //
    }
  }, [handleSaveRole, isEditMode, onSetVisible, queryClient, state]);

  const handleClose = useCallback(() => {
    setIsPrevent(false);
    setSuccessModal(false);
    handleReset();
    history.push(ROUTES.ROLES_AND_SECURITY);
  }, [handleReset, history]);
  return (
    <>
      <PreventLeaveModal
        subText="By leaving now, your progress will not be saved. "
        isPrevent={!!isDirty && !!isPrevent}
      />
      <SuccessModal
        testId="role-setup_success"
        visible={successModal}
        header={`${state.name} has been saved!`}
        buttonText="Go To Roles and Security"
        onSetVisible={handleClose}
      />
      <Box style={{ zIndex: 10000 }}>
        <ErrorModal
          testId="EmployersList_server-error"
          visible={isServerError}
          header={SERVER_ERROR_MODAL_TITLE}
          helpText={SERVER_ERROR_MODAL_TEXT}
          buttonText="Close"
          buttonTextTryAgain="Try again"
          onSetVisible={setIsServerError}
          onSubmit={handleConfirm}
        />
      </Box>
      <Modal
        testId="role-setup_confirm"
        visible={visible}
        onSetVisible={onSetVisible}
        wrapperStyle={{ zIndex: 1003 }}
      >
        <RoleSetupForm
          isModalMode
          isViewMode
          title={
            <Text size="2xl" weight={700} margin={{ bottom: 'l' }} style={{ lineHeight: '32px' }}>
              Save {state.name}
            </Text>
          }
          onSubmit={handleSubmit}
          defaultValues={formattedData}
          footer={
            <Box margin={{ top: 'l' }} direction="row" justify="end">
              <AppButton
                testId="role-confirm_cancel"
                width="160px"
                buttonType="secondary"
                onClick={handleToggleVisible}
              >
                Cancel
              </AppButton>
              <Box margin={{ left: 's' }}>
                <AppButton
                  testId="role-confirm_confirm"
                  disabled={isLoading}
                  width="160px"
                  onClick={handleConfirm}
                  buttonType="primary"
                >
                  {isLoading ? <Preloader /> : 'Confirm'}
                </AppButton>
              </Box>
            </Box>
          }
        >
          <>
            <AffectedUsersCountSection
              isLoading={isLoading}
              isEditMode
              organizationId={state.organizationId}
              roleId={state.id}
            />
            {state.id !== NO_ACCESS_ROLE_ID && <FieldLevelAccessSection id={state.fieldLevelAccessId} />}

            <Box
              round="container1Round"
              background="canvas"
              margin={{ top: 'l' }}
              pad="l"
              border={{ size: 'small', color: 'border1' }}
            >
              <PermissionsSection revoked={revoked} granted={granted} />
            </Box>
          </>
        </RoleSetupForm>
      </Modal>
    </>
  );
};

export default RoleSetupConfirmModal;
